import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, makeStyles } from '@material-ui/core'
import { useTranslate } from 'react-admin'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import TodayIcon from '@material-ui/icons/Today'
import { addMonths, format, isSameMonth, isToday, startOfDay, startOfMonth } from 'date-fns'
import LoaderCircle from '../../../components/loaders/LoaderCircle/LoaderCircle'
import mobileScheduleStyles from './mobileSchedule.styles'
import PAIRED_EVENT_STRIPES from '../../../utils/constants/pairedEventStyle'

const useStyles = makeStyles(mobileScheduleStyles)

// Light event-type colours (the lime of a game) can't carry white text.
const textColorFor = (hex) => {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex || '')
  if (!match) return '#FFFFFF'
  const value = parseInt(match[1], 16)
  const [r, g, b] = [(value >> 16) & 255, (value >> 8) & 255, value & 255]
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? 'rgba(0, 0, 0, 0.87)' : '#FFFFFF'
}

const formatTime = (date) => format(date, 'HH:mm')

/**
 * The calendar on a phone, laid out like Google Calendar's schedule: one month at a time, a row
 * per day that has events (today always), the day on the left and its events as cards. Read-only;
 * a card opens the event's details.
 */
const MobileSchedule = ({ events, loading, onMonthChange, onEventClick }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const [month, setMonth] = useState(() => startOfMonth(new Date()))
  const todayRef = useRef()
  const scrolledToToday = useRef(false)

  useEffect(() => {
    onMonthChange({ start: month, end: addMonths(month, 1) })
  }, [month])

  const days = useMemo(() => {
    const byDay = new Map()
    const addDay = (day) => {
      const key = day.getTime()
      if (!byDay.has(key)) byDay.set(key, { day, events: [] })
      return byDay.get(key)
    }

    ;(events || [])
      .map((event) => ({ ...event, start: new Date(event.start), end: new Date(event.end) }))
      .filter((event) => isSameMonth(event.start, month))
      .sort((a, b) => a.start - b.start)
      .forEach((event) => addDay(startOfDay(event.start)).events.push(event))

    if (isSameMonth(new Date(), month)) addDay(startOfDay(new Date()))

    return [...byDay.values()].sort((a, b) => a.day - b.day)
  }, [events, month])

  // Open on today, once: later months and refetches leave the scroll where the user put it.
  useEffect(() => {
    if (scrolledToToday.current || loading || !todayRef.current) return
    scrolledToToday.current = true
    todayRef.current.scrollIntoView({ block: 'start' })
  }, [days, loading])

  const goToToday = () => {
    setMonth(startOfMonth(new Date()))
    scrolledToToday.current = false
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.monthTitle}>{format(month, 'MMMM yyyy')}</div>
        <LoaderCircle loading={loading} />
        <IconButton size='small' aria-label='Today' onClick={goToToday}>
          <TodayIcon />
        </IconButton>
        <IconButton
          size='small'
          aria-label='Previous month'
          onClick={() => setMonth(addMonths(month, -1))}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          size='small'
          aria-label='Next month'
          onClick={() => setMonth(addMonths(month, 1))}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      {!loading && days.length === 0 && (
        <div className={classes.empty}>{translate('ra.text.nothingPlannedIn', { month: format(month, 'MMMM') })}</div>
      )}

      {days.map(({ day, events: dayEvents }) => {
        const today = isToday(day)
        return (
          <div key={day.getTime()} className={classes.dayRow} ref={today ? todayRef : undefined}>
            <div className={classes.dayLabel}>
              <div className={today ? classes.weekdayToday : classes.weekday}>
                {format(day, 'EEE')}
              </div>
              <div className={today ? classes.dayNumberToday : classes.dayNumber}>
                {format(day, 'd')}
              </div>
            </div>
            <div className={classes.dayEvents}>
              {dayEvents.length === 0 && <div className={classes.nothingToday}>{translate('ra.text.nothingPlanned')}</div>}
              {dayEvents.map((event) => (
                <button
                  key={event.id}
                  type='button'
                  className={classes.eventCard}
                  style={{
                    backgroundColor: event.color,
                    color: textColorFor(event.color),
                    ...(event.paired && { backgroundImage: PAIRED_EVENT_STRIPES }),
                  }}
                  onClick={() => onEventClick(event.id)}
                >
                  <span className={classes.eventTitle}>{event.title}</span>
                  <span className={classes.eventTime}>
                    {`${formatTime(event.start)} – ${formatTime(event.end)}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

MobileSchedule.propTypes = {
  events: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      start: PropTypes.string,
      end: PropTypes.string,
      color: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  onMonthChange: PropTypes.func.isRequired,
  onEventClick: PropTypes.func.isRequired,
}

export default MobileSchedule
